import React, { useEffect } from "react";
import { ArrowRight, CheckCircle2, Stethoscope } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { DOCTOR_NETWORK } from "@/lib/site-data";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function ForDoctorsPage() {
 useEffect(() => {
 window.scrollTo(0, 0);
 }, []);

 useDocumentMeta({
 title: "Join the TeleMedCare clinical network - Apply as a doctor",
 description:
 "See patients on your schedule with AI-assisted documentation, encrypted records and weekly transparent payouts. Join a vetted network of licensed Nigerian doctors.",
 canonical: "https://telemedcare.app/for-doctors",
 });

 const { hero, stats, requirements, process, applyCta } = DOCTOR_NETWORK;

 return (
 <div className="min-h-screen bg-cream text-ink">
 <Navbar />
 <main className="pt-[72px]">
 <section className="relative overflow-hidden bg-ink text-cream">
 <div
 aria-hidden="true"
 className="absolute -top-32 right-0 h-[420px] w-[420px] rounded-full bg-clay/25 blur-3xl"
 />
 <div className="container-x relative py-16 lg:py-24">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
 <div className="lg:col-span-6">
 <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-clay">
 {hero.eyebrow}
 </p>
 <h1 className="mt-4 font-display text-[40px] sm:text-[52px] lg:text-[60px] font-extrabold leading-[1.05] tracking-tight text-balance">
 {hero.title}
 </h1>
 <p className="mt-6 text-[17px] text-cream/85 max-w-xl leading-relaxed">
 {hero.body}
 </p>
 <div className="mt-10 flex flex-wrap gap-3">
 <a
 href={applyCta.href}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="doctor-apply-primary"
 className="inline-flex items-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-7 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
 >
 <Stethoscope className="h-5 w-5" />
 {applyCta.label}
 <ArrowRight className="h-4 w-4" />
 </a>
 </div>
 <p className="mt-4 text-[13.5px] text-cream/60 max-w-md">
 {applyCta.helper}
 </p>
 </div>
 <div className="lg:col-span-6">
 <div className="relative">
 <div
 aria-hidden="true"
 className="absolute -inset-3 -z-10 rounded-[32px] bg-clay/20"
 style={{ transform: "rotate(1.5deg)" }}
 />
 <img
 src={hero.image}
 alt={hero.image_alt}
 className="w-full rounded-3xl object-contain bg-cream aspect-[5/4] shadow-2xl"
 />
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="border-b border-ink/10 bg-cream py-14 lg:py-16">
 <div className="container-x">
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
 {stats.map((s) => (
 <div key={s.label} data-testid={`doctor-stat-${s.value}`}>
 <p className="font-display text-[32px] sm:text-[40px] font-extrabold text-forest leading-none">
 {s.value}
 </p>
 <p className="mt-3 text-[14.5px] text-ink/70 leading-snug max-w-[220px]">
 {s.label}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 <section className="py-20 lg:py-28">
 <div className="container-x">
 <div className="max-w-3xl">
 <h2 className="font-display text-[32px] sm:text-[40px] font-extrabold tracking-tight text-balance">
 {requirements.title}
 </h2>
 <p className="mt-4 text-[17px] text-ink/70 leading-relaxed">
 {requirements.subtitle}
 </p>
 </div>
 <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
 {requirements.items.map((item) => (
 <li
 key={item.title}
 className="rounded-2xl border border-ink/10 bg-white p-6 lg:p-8 shadow-sm"
 >
 <div className="flex gap-4">
 <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest/10 text-forest">
 <CheckCircle2 className="h-5 w-5" />
 </span>
 <div>
 <h3 className="font-display text-[18px] font-bold text-ink">
 {item.title}
 </h3>
 <p className="mt-2 text-[15px] text-ink/70 leading-relaxed">
 {item.detail}
 </p>
 </div>
 </div>
 </li>
 ))}
 </ul>
 </div>
 </section>

 <section className="bg-ink py-20 lg:py-28 text-cream">
 <div className="container-x">
 <h2 className="font-display text-[32px] sm:text-[40px] font-extrabold tracking-tight max-w-2xl">
 {process.title}
 </h2>
 <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
 {process.steps.map((s) => (
 <li key={s.step} className="relative">
 <span className="font-display text-[48px] font-extrabold text-clay/90 leading-none">
 {s.step}
 </span>
 <h3 className="mt-4 font-display text-[20px] font-bold">
 {s.title}
 </h3>
 <p className="mt-2 text-[15px] text-cream/75 leading-relaxed">
 {s.body}
 </p>
 </li>
 ))}
 </ol>
 <div className="mt-14 flex flex-wrap items-center gap-4">
 <a
 href={applyCta.href}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="doctor-apply-bottom"
 className="inline-flex items-center gap-2 rounded-full bg-clay hover:bg-clayHover text-cream px-7 py-3.5 text-[15px] font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
 >
 {applyCta.label}
 <ArrowRight className="h-4 w-4" />
 </a>
 <p className="text-[14px] text-cream/65 max-w-md">
 Questions before you apply? You can also start a chat with our team on WhatsApp from the home page.
 </p>
 </div>
 </div>
 </section>
 </main>
 <Footer />
 </div>
 );
}
